import { useMemo, useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import {
  type Obj,
  duration,
  money,
  number,
  evaluation,
  label,
  outcome,
  checkpointFindings,
} from "./api";
import { Badge, Empty } from "./ui";
import { ModelVisibility, useProfilePalette, DataMark, type Palette } from "./AnalyticsCharts";

const colors = ["var(--cg-primitives-chart-1-teal)", "var(--cg-primitives-chart-2-violet)", "var(--cg-primitives-chart-3-amber)", "var(--cg-primitives-chart-4-rose)", "var(--cg-primitives-chart-5-blue)"];
export function Analytics({
  run,
  onInspect,
}: {
  run: Obj | null;
  onInspect: (caseId: string, modelId: string) => void;
}) {
  const [slice, setSlice] = useState("all");
  const allModels: Obj[] = run?.manifest?.target_models || [];
  const [selectedModels, setSelectedModels] = useState<string[] | null>(null);
  useEffect(() => { setSelectedModels(null); setSlice("all"); }, [run?.id]);
  const color = useProfilePalette(allModels, "id");
  const visible = new Set(selectedModels ?? allModels.map(m => m.id));
  const models = allModels.filter(m => visible.has(m.id));
  const conversations: Obj[] = run?.conversations || [];
  const results: Obj[] = (run?.results || []).filter((r: Obj) => visible.has(r.model_id));
  const rows = useMemo(
    () =>
      conversations.filter((c) => visible.has(c.model_id) && (slice === "all" || c.case.language === slice)),
    [conversations, slice, selectedModels],
  );
  const base = allModels.find(m => m.id === (run?.config?.baseline_id || run?.config?.baseline_model_id)) || allModels[0];
  const singleModel = allModels.length === 1;
  if (!run)
    return (
      <Empty title="See Results After Your First Run">
        Run a comparison to measure response time, quality, and estimated cost.
      </Empty>
    );
  function paired(id: string) {
    let wins = 0,
      losses = 0,
      ties = 0,
      ungraded = 0;
    const byKey = new Map(
      rows
        .filter((c) => c.model_id === id || c.model_id === base?.id)
        .map((c) => [c.case.id + ":" + c.ordinal, c]),
    );
    [...byKey.values()].forEach((reference) => {
      const c = rows.find(
        (x) =>
          x.model_id === id &&
          x.ordinal === reference.ordinal &&
          x.case.id === reference.case.id,
      );
      const b = rows.find(
        (x) =>
          x.model_id === base?.id &&
          x.ordinal === reference.ordinal &&
          x.case.id === reference.case.id,
      );
      const a = evaluation(c),
        be = evaluation(b);
      if (a.status !== "evaluated" || be.status !== "evaluated") ungraded++;
      else if (a.correct === be.correct) ties++;
      else if (a.correct) wins++;
      else losses++;
    });
    return { wins, losses, ties, ungraded };
  }
  const mode = singleModel
    ? "Review quality, response time, and cost for your saved model."
    : run.config?.mode === "adaptive"
      ? "Compare the same scenarios. Simulated customer replies can differ between models."
      : "Each model receives the same saved conversation checkpoints.";
  return (
    <div className="analytics">
      <div className="analytics-heading">
        <div>
          <h2>{singleModel ? "Model Analytics" : "Comparison Analytics"}</h2>
          <p>{mode}</p>
        </div>
        <Badge tone="warning">Exploratory Results</Badge>
        <select
          aria-label="Filter quality by language"
          value={slice}
          onChange={(e) => setSlice(e.target.value)}
        >
          <option value="all">All Languages</option>
          {[...new Set(conversations.map((c) => c.case.language))].map((l) => (
            <option key={l} value={l}>
              {label(l)}
            </option>
          ))}
        </select>
      </div>
      <ModelVisibility roster={allModels} selected={selectedModels} onChange={setSelectedModels} color={color} keyField="id" />
      {!models.length && <Empty title="No Models Selected">Use All above to restore the model charts and tables.</Empty>}
      <div className="analytics-table-wrap">
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Saved Model</th>
              <th>Pass / Graded</th>
              <th>Graded / Recorded</th>
              <th>Known Critical Failures</th>
              <th>Checkpoint Findings</th>
              {!singleModel && <th>vs Baseline · Win / Loss / Tie</th>}
              <th>p50 Response Time</th>
              <th>p95 Response Time</th>
              <th>p99 Response Time</th>
              <th>p95 First Text</th>
              <th>Estimated Cost</th>
            </tr>
          </thead>
          <tbody>
            {models.map((m) => {
              const r = results.find((x) => x.model_id === m.id) || {};
              const cs = rows.filter((c) => c.model_id === m.id);
              const graded = cs.filter(
                (c) => evaluation(c).status === "evaluated",
              );
              const p = singleModel ? null : paired(m.id);
              const findings = cs.map(checkpointFindings);
              const partial = cs.filter(
                (c) =>
                  evaluation(c).status !== "evaluated" &&
                  (checkpointFindings(c).graded > 0 ||
                    checkpointFindings(c).failed > 0),
              ).length;
              const knownCritical = cs.filter(
                (c) => evaluation(c).critical,
              ).length;
              const failedCheckpoints = findings.reduce(
                (sum, f) => sum + f.failed,
                0,
              );
              const criticalCheckpoints = findings.reduce(
                (sum, f) => sum + f.critical,
                0,
              );
              return (
                <tr key={m.id}>
                  <td>
                    <strong>
                      <i style={{ background: color(m.id) }} />
                      {m.name}
                    </strong>
                    <code>{m.model_id}</code>
                    {m.id === base?.id && <Badge>Baseline</Badge>}
                    {(r.demonstration ||
                      m.demonstration ||
                      run.manifest?.demonstration_model_ids?.includes(
                        m.id,
                      )) && <Badge tone="warning">Scripted Demonstration</Badge>}
                  </td>
                  <td>
                    {graded.filter((c) => evaluation(c).correct).length} /{" "}
                    {graded.length}
                  </td>
                  <td>
                    {graded.length} / {cs.length}
                    <small>
                      {partial > 0 ? `${partial} partially evaluated · ` : ""}
                      {cs.length - graded.length - partial} not evaluated
                    </small>
                  </td>
                  <td className={knownCritical > 0 ? "red" : ""}>
                    {knownCritical}
                    <small>Conversations, including partial findings</small>
                  </td>
                  <td className={failedCheckpoints > 0 ? "red" : ""}>
                    {failedCheckpoints} failed
                    <small>{criticalCheckpoints} critical checkpoints</small>
                  </td>
                  {!singleModel && (
                    <td>
                      {m.id === base?.id ? (
                        "—"
                      ) : !visible.has(base?.id) ? ("Baseline hidden") : run.config?.mode === "adaptive" ? (
                        "Scenario comparison only"
                      ) : (
                        <>
                          <span className="teal">{p?.wins}</span> /{" "}
                          <span className="red">{p?.losses}</span> / {p?.ties}
                          <small>{p?.ungraded} unmatched or ungraded</small>
                        </>
                      )}
                    </td>
                  )}
                  <td>{duration(r.p50_ms)}</td>
                  <td>{duration(r.p95_ms)}</td>
                  <td>{duration(r.p99_ms)}</td>
                  <td>
                    {duration(r.ttft_p95_ms)}
                    <small>
                      {
                        r.ttft_samples ?? conversations.filter(c => c.model_id === m.id)
                          .flatMap((c) => c.turns || [])
                          .filter((t: Obj) => t.ttft_ms != null).length
                      }{" "}
                      samples
                    </small>
                  </td>
                  <td>{money(r.cost_usd)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="b-muted">
        The language filter applies to the quality table and scenario results. Timing and cost cover the
        full run. Timing includes successful model requests; failures and
        timeouts are counted separately. Cost is estimated from saved rates.
        Known failures stay visible when other checkpoints remain ungraded.
        Partial evaluations never count as a pass.
      </p>
      <div
        className={"analytics-grid" + (singleModel ? " single-profile" : "")}
      >
        <section className="analytic-panel">
          <div className="section-toolbar">
            <h3>Response Time Distribution</h3>
            <span>Successful responses</span>
          </div>
          <LatencyPlot rows={conversations.filter(c => visible.has(c.model_id))} models={models} color={color} />
        </section>
        {!singleModel && (
          <section className="analytic-panel">
            <div className="section-toolbar">
              <h3>Quality and Cost</h3>
              <span>Full run · selected models</span>
            </div>
            <QualityPlot results={results} models={models} color={color} />
          </section>
        )}
      </div>
      <section className="analytic-panel">
        <div className="section-toolbar">
          <div>
            <h3>Measured Load Over Time · Full Run</h3>
            <p>
              This shared run-level view is unaffected by model visibility. Active conversations are ongoing customer dialogues. Active requests
              are model replies being generated.
            </p>
          </div>
          <Badge>{run.config?.concurrency} active conversations planned</Badge>
        </div>
        <LoadPlot
          samples={run.progress?.load_samples || []}
          target={run.config?.concurrency || 1}
        />
        <div className="metric-strip">
          <Metric
            label="Peak Active Conversations"
            value={number(run.progress?.peak_active_conversations)}
          />
          <Metric
            label="Peak Active Requests"
            value={number(run.progress?.peak_inflight_requests)}
          />
          <Metric
            label="Completed Requests"
            value={number(run.progress?.completed_requests)}
          />
          <Metric
            label="Reserved Spend"
            value={money(run.progress?.reserved_usd)}
          />
        </div>
        <p className="b-muted">
          {run.manifest?.demonstration
            ? "Includes scripted demonstrations, which are labeled separately from real provider results."
            : "Use measured load to assess provider capacity; the planned number alone does not prove it."}{" "}
          {run.config?.profile === "quality"
            ? "This run measures quality. It does not establish production capacity."
            : "Review sustained conversation load and provider errors together."}
        </p>
      </section>
      <div className="analytics-grid">
        <section className="analytic-panel">
          <h3>Errors and Request Delays</h3>
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Saved Model</th>
                <th>Failed Requests</th><th>Pending / Unknown</th>
                <th>Retries</th>
                <th>Rate Limits</th>
                <th>p95 Queue Wait</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.model_id}>
                  <td>{r.name}</td>
                  <td>{number(r.failed_requests)}</td><td>{number(r.pending_requests)} / {number(r.unknown_requests)}</td>
                  <td>{number(r.retries)}</td>
                  <td>{number(r.throttles)}</td>
                  <td>{duration(r.queue_p95_ms)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="analytic-panel">
          <h3>Readiness for Production</h3>
          <p className="b-muted">
            Review these gaps before using the results for a production decision.
          </p>
          {(
            run.qualification?.reasons || [
              "Human-reviewed references are needed to check the judges’ accuracy.",
            ]
          ).map((r: string) => (
            <div className="readiness-row" key={r}>
              <Badge tone="warning">Open</Badge>
              <span>{r}</span>
            </div>
          ))}
        </section>
      </div>
      <section className="analytic-panel">
        <h3>Customer Realism</h3>
        <p className="b-muted">
          {run.config?.mode === "replay"
            ? "Replay uses saved customer turns. It does not assess the Conversation Simulator."
            : "The Realism Judge compares simulated dialogue with anonymized sources. Its findings still need human validation and are separate from model quality."}
        </p>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Saved Model</th>
              <th>AI Review Passed</th>
              <th>Failed</th>
              <th>Ungraded</th>
              <th>Not Applicable</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.model_id}>
                <td>{r.name}</td>
                <td>{number(r.realism?.ai_screen_passed)}</td>
                <td>{number(r.realism?.failed)}</td>
                <td>{number(r.realism?.ungraded)}</td>
                <td>{number(r.realism?.not_applicable)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <section className="analytic-panel">
        <h3>Usage and Estimated Cost by Role · Full Run</h3><p className="b-muted">Shared judge and simulator usage cannot be allocated to selected models. This audit table always shows the full run; selected model costs appear above.</p>
        <table className="analytics-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Requests</th>
              <th>Input Tokens</th>
              <th>Output Tokens</th>
              <th>Estimated Cost</th>
              <th>Unreported Usage</th>
              <th>Reserved Spend</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(run.analytics?.cost_by_role || {}).map(
              ([role, value]) => {
                const v = value as Obj;
                return (
                  <tr key={role}>
                    <td>
                      {role === "target"
                        ? "Tested Models"
                        : role === "simulator"
                          ? "Conversation Simulator"
                          : role === "realism_judge"
                            ? "Realism Judge"
                            : "Quality Judge"}
                    </td>
                    <td>{number(v.requests)}</td>
                    <td>{number(v.input_tokens)}</td>
                    <td>{number(v.output_tokens)}</td>
                    <td>{money(v.estimated_cost_usd)}</td>
                    <td>{number(v.unknown_usage_requests)}</td>
                    <td>{money(v.held_reserve_usd)}</td>
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
        {!run.analytics?.cost_by_role && (
          <p className="b-muted">
            Cost by role is unavailable for this older run.
          </p>
        )}
      </section>
      <section className="analytic-panel">
        <h3>Scenario Results</h3>
        <p className="b-muted">
          Select a result to read the exact transcript and evaluation.
        </p>
        <table className="analytics-table scenario-matrix">
          <thead>
            <tr>
              <th>Scenario</th>
              {models.map((m) => (
                <th key={m.id}>{m.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...new Map(rows.map((c) => [c.case.id, c.case])).values()].map(
              (c) => (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    <small>
                      {c.language} · {c.source_group}
                    </small>
                  </td>
                  {models.map((m) => {
                    const conv = rows.find(
                      (x) => x.case.id === c.id && x.model_id === m.id,
                    );
                    return (
                      <td key={m.id}>
                        <button
                          onClick={() => onInspect(c.id, m.id)}
                          className="b-link"
                        >
                          {outcome(conv)}
                          <ArrowUpRight size={12} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function LatencyPlot({ rows, models, color }: { rows: Obj[]; models: Obj[]; color: Palette }) {
  const [metric, setMetric] = useState("latency_ms");
  const series = models.map((m) => ({
    m,
    values: rows
      .filter((c) => c.model_id === m.id)
      .flatMap((c) => (c.turns || []).map((t: Obj) => t[metric]))
      .filter((n: unknown) => typeof n === "number")
      .sort((a: number, b: number) => a - b),
  }));
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  return (
    <div className="distribution">
      <select
        aria-label="Response time measure"
        value={metric}
        onChange={(e) => setMetric(e.target.value)}
      >
        <option value="latency_ms">Full Response Time</option>
        <option value="ttft_ms">Time to First Text</option>
      </select>
      {!series.some((s) => s.values.length) && (
        <p className="b-muted">
          No measurements are available for this response-time measure.
        </p>
      )}
      {series.map((s) => (
        <div key={s.m.id}>
          <span>
            {s.m.name}
            <small>n = {s.values.length}</small>
          </span>
          <svg className="distribution-track distribution-svg" viewBox="0 0 400 32" role="group" aria-label={s.m.name + " measured response-time range"}>
            {s.values.map((v: number, j: number) => <DataMark key={j} x={12+(v/max)*376} y={16} color={color(s.m.id)} radius={4} label={`${s.m.name} · ${metric === "ttft_ms" ? "Time to first text" : "Complete response"}: ${duration(v)} · response ${j+1} of ${s.values.length} measured samples`} />)}
          </svg>
          <strong>
            {s.values.length
              ? duration((s.values[Math.floor((s.values.length - 1) / 2)] + s.values[Math.ceil((s.values.length - 1) / 2)]) / 2)
              : "—"}
          </strong>
        </div>
      ))}
      <footer>
        <span>0 ms</span>
        <span>{duration(max)}</span>
      </footer>
      <p className="b-muted">
        Each mark is a measured response; hover or focus for details. Median shown on the right. First-text timing excludes earlier queue and retry waits; complete-response timing includes them.
      </p>
    </div>
  );
}
function QualityPlot({ results, models, color }: { results: Obj[]; models: Obj[]; color: Palette }) {
  const valid = results.filter(
    (r) =>
      r.correct + r.incorrect > 0 &&
      r.ungraded === 0 &&
      r.cost_complete === true &&
      typeof r.cost_usd === "number",
  );
  if (valid.length < 1)
    return (
      <Empty title="Complete Grades and Settled Cost Needed">
        A visible model needs complete recorded grades and resolved target usage. Pending or unreported charges are not zero-cost evidence.
      </Empty>
    );
  const max = Math.max(0.00001, ...valid.map((r) => r.cost_usd));
  return (
    <div>
      <svg
        className="scatter"
        viewBox="0 0 480 210"
        role="group"
        aria-label="Quality rate against estimated cost"
      >
        <line x1="45" y1="175" x2="450" y2="175" stroke="var(--cg-border-strong)" />
        <line x1="45" y1="20" x2="45" y2="175" stroke="var(--cg-border-strong)" />
        <text x="3" y="27">
          100%
        </text>
        <text x="16" y="179">
          0%
        </text>
        {valid.map((r) => {
          const x = 60 + (r.cost_usd / max) * 370,
            y = 175 - (r.correct / (r.correct + r.incorrect)) * 145;
          return (
            <DataMark key={r.model_id} x={x} y={y} color={color(r.model_id)} radius={6} label={`${r.name} · ${(100*r.correct/(r.correct+r.incorrect)).toFixed(1)}% pass (${r.correct}/${r.correct+r.incorrect} graded) · ${money(r.cost_usd)} estimated target cost
${valid.filter(q => q.model_id !== r.model_id && Math.abs(q.cost_usd-r.cost_usd)/max < .04 && Math.abs(q.correct/(q.correct+q.incorrect)-r.correct/(r.correct+r.incorrect)) < .08).map(q=>`Nearby: ${q.name} · ${(100*q.correct/(q.correct+q.incorrect)).toFixed(1)}% pass · ${money(q.cost_usd)}`).join("\n")}`} />

          );
        })}
        <text x="55" y="194">$0</text><text x="425" y="194" textAnchor="end">{money(max)}</text><text x="170" y="209">
          Estimated Model Cost →
        </text>
      </svg>
      <div className="ga-chart-legend">{models.map(m=><span key={m.id}><i style={{background:color(m.id)}} />{m.name}</span>)}</div>
      <p className="b-muted">
        Pass rate includes models whose recorded conversations are all graded. Planned conversation coverage can still be incomplete. Models with pending or unknown target cost are excluded. It does not prove
        equivalent quality.
      </p>
    </div>
  );
}
function LoadPlot({ samples, target }: { samples: Obj[]; target: number }) {
  if (!samples.length)
    return (
      <Empty title="No Load Measurements Yet">
        Active conversations and requests appear here during a run.
      </Empty>
    );
  const maxX = Math.max(1, ...samples.map((s) => s.elapsed_seconds || 0));
  const maxY = Math.max(
    target,
    1,
    ...samples.map((s) =>
      Math.max(
        s.active_conversations || 0,
        s.inflight_requests || 0,
        s.queued_requests || 0,
      ),
    ),
  );
  const path = (key: string) =>
    samples
      .filter((s) => typeof s[key] === "number")
      .map(
        (s, i) =>
          (i ? "L" : "M") +
          (42 + ((s.elapsed_seconds || 0) / maxX) * 830).toFixed(1) +
          "," +
          (174 - (s[key] / maxY) * 150).toFixed(1),
      )
      .join(" ");
  return (
    <>
      <svg
        className="load-chart"
        viewBox="0 0 920 210"
        role="img"
        aria-label="Measured active conversations and model requests over time"
      >
        <line x1="42" y1="174" x2="880" y2="174" stroke="var(--cg-border-strong)" />
        <line
          x1="42"
          y1={174 - (target / maxY) * 150}
          x2="880"
          y2={174 - (target / maxY) * 150}
          stroke="var(--cg-icon-tertiary)"
          strokeDasharray="4 4"
        />
        <text x="4" y="28">
          {maxY}
        </text>
        <text x="18" y="177">
          0
        </text>
        <path
          d={path("active_conversations")}
          fill="none"
          stroke={colors[0]}
          strokeWidth="2.5"
        />
        <path
          d={path("inflight_requests")}
          fill="none"
          stroke={colors[1]}
          strokeWidth="2"
        />
        {samples.some((s) => s.queued_requests != null) && (
          <path
            d={path("queued_requests")}
            fill="none"
            stroke={colors[2]}
            strokeWidth="2"
          />
        )}
        <text x="42" y="199">
          0s
        </text>
        <text x="837" y="199">
          {Math.round(maxX)}s
        </text>
      </svg>
      <div className="chart-legend">
        <span>
          <i style={{ background: colors[0] }} />
          Active conversations
        </span>
        <span>
          <i style={{ background: colors[1] }} />
          Active requests
        </span>
        <span>Dashed line: planned conversations</span>
        {!samples.some((s) => s.queued_requests != null) && (
          <span>Queue measurements unavailable</span>
        )}
      </div>
    </>
  );
}
